import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiButton,
  MiCheckbox,
  MiDetailFields,
  MiInputTextbox,
  MiMenu,
  MiModal,
  MiReorderButton,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
} from "@miplatform/constants";
import { getUpdatedFields } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  useTheme,
} from "@mui/material";
import { CollapsibleIcon } from "../../../../components/CollapsibleIcon/CollapsibleIcon";
import { MaterialCardList } from "../../../../components/MaterialCardList/MaterialCardList";
import { usePlanAreaViewModel } from "./usePlanAreaViewModel";

const schema = z.object({
  label: z.string().min(1, "Area Name is required"),
});

export const PlanArea = ({
  area = {},
  areaType,
  updateArea,
  planDetails,
  selectedAreaId = null,
  setSelectedAreaId = () => {},
  dismissedCategories = [],
  handleDismissCategory,
  checkedAreas = [],
  handleCheckArea = () => {},
  handleRemoveArea = () => {},
  handleResetAddItems,
  handleCreateSpecPlan = () => null,
  selectedPlaceholder = null,
  setSelectedPlaceholder,
  categoryFilterOptions,
  setRequiredCategoriesMatch = () => {},
  handleCategoryClick,
  handleOpenCategoryFilter,
  showAreaDrawer,
  showCategoryFilter,
  openMaterialResults,
  openItemTemplateResults,
  isLoading,
  stateManager,
  index,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [openItemTemplateModal, setOpenItemTemplateModal] = useState(false);
  const [itemTemplateLabel, setItemTemplateLabel] = useState("");

  const {
    materials,
    setMaterials,
    getMaterials,
    handleMoveUpArea,
    handleMoveDownArea,
    handleCreateItemTemplate,
    handleCloseItemTemplateModal,
  } = usePlanAreaViewModel({
    area,
    handleCreateSpecPlan,
    setOpenItemTemplateModal,
    setItemTemplateLabel,
    planDetails,
    itemTemplateLabel,
    stateManager,
  });
  const { classes } = useStyles({});
  const theme = useTheme();

  const {
    formState: { dirtyFields, isValid },
    control,
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: { label: area.label || "" },
    resolver: zodResolver(schema),
  });

  const isEditing = selectedAreaId && selectedAreaId === area.id;
  const isAreaSelected = checkedAreas?.some((a) => a.id === area.id) ?? false;
  const showMenu = !area.preview;
  const accordionClasses = `${classes.accordionContainer} ${
    area.preview ? classes.previewAccordion : ""
  }`;

  useEffect(() => {
    if (area.materials?.length > 0) {
      getMaterials();
    } else {
      setMaterials([]);
    }
  }, [area.materials]);

  useEffect(() => {
    const requiredCategoriesOnly = area.requiredCategories?.filter(
      (category) => category.isRequired
    );
    const allCategoriesMatch = requiredCategoriesOnly?.every((category) =>
      materials.some((item) => {
        return (
          item.category.categoryId === category.categoryId ||
          item.categoryId === category.categoryId
        );
      })
    );
    if (!area.preview) {
      setRequiredCategoriesMatch((prev) => ({
        ...prev,
        [area.id]: allCategoriesMatch,
      }));
    }
  }, [materials]);

  useEffect(() => {
    if (isAreaSelected) {
      setIsExpanded(true);
    }
  }, [isAreaSelected]);

  const areaFields = [
    {
      className: classes.areaLabelFieldRoot,
      name: "label",
      label: isEditing ? areaType?.name ?? "Area Name" : "",
      formControl: control,
      disabled: !isEditing,
      padding: 0,
      readOnly: !isEditing,
    },
  ];

  const handleUpdateArea = () => {
    const updatedArea = getUpdatedFields(dirtyFields, getValues());
    updateArea(area.id, updatedArea);
    setSelectedAreaId(null);
  };

  const handleCloseAreaEdit = () => {
    setSelectedAreaId(null);
    reset({ label: area.label });
  };

  const menuOptions = [
    {
      label: "Edit Area Name",
      icon: mdi[ICONS_WEB.PENCIL_OUTLINE],
      action: () => setSelectedAreaId(area.id),
    },
    {
      label: "Save as Item Template",
      icon: mdi[ICONS_WEB.TEXTBOX_PLUS_OUTLINE],
      action: () => setOpenItemTemplateModal(true),
      disabled: area?.materials?.length === 0,
    },
    {
      label: "Remove Area",
      icon: mdi[ICONS_WEB.TRASH_CAN],
      action: () => handleRemoveArea(area),
      disabled: area?.materials?.length > 0 || showCategoryFilter,
    },
  ];

  return (
    <Accordion
      className={accordionClasses}
      expanded={isExpanded}
      disableGutters
      style={{
        border:
          isAreaSelected && !isEditing
            ? `2px solid ${theme.palette.orange.main}`
            : "2px solid transparent",
      }}
    >
      <AccordionSummary className={classes.accordionSummary}>
        <Box className={classes.areaActionsContainer}>
          <Box display="flex" flex={1}>
            {(openMaterialResults || openItemTemplateResults) && (
              <MiCheckbox
                value={isAreaSelected}
                handleChange={() => handleCheckArea(area, !isAreaSelected)}
                className={classes.checkbox}
              />
            )}
            <MiDetailFields
              detailFields={areaFields}
              containerStyle={classes.areaDetailContainer}
            />
          </Box>
          <Box display="flex" alignItems="center">
            {isEditing && (
              <Box className={classes.editButtons}>
                <MiButton
                  startIcon={{
                    path: mdi[ICONS_WEB.CLOSE],
                    iconSize: 0.7,
                    iconColor: theme.palette.error.main,
                  }}
                  onClick={handleCloseAreaEdit}
                />
                <MiButton
                  startIcon={{
                    path: mdi[ICONS_WEB.CHECK],
                    iconSize: 0.7,
                    iconColor: theme.palette.primary.main,
                  }}
                  disabled={!isValid}
                  onClick={handleUpdateArea}
                />
              </Box>
            )}
            {!isEditing && (
              <Box className={classes.rightActionButtons}>
                <MiButton
                  title="Add Items"
                  variant={BUTTON_VARIANTS.TEXT}
                  startIcon={{
                    path: mdi[ICONS_WEB.PLUS],
                    iconSize: 0.8,
                  }}
                  onClick={handleOpenCategoryFilter}
                  disabled={showAreaDrawer || isLoading}
                />
                <MiReorderButton
                  onMoveUp={() => handleMoveUpArea(index)}
                  onMoveDown={() => handleMoveDownArea(index)}
                  disabled={isLoading}
                />
                <CollapsibleIcon
                  isCollapsed={isExpanded}
                  onCollapse={() => setIsExpanded((prev) => !prev)}
                />
              </Box>
            )}
            {showMenu && (
              <MiMenu
                showHover
                data={menuOptions}
                testID={`area-menu-${area.id}`}
                disabled={isEditing || isLoading}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <MaterialCardList
          planDetails={planDetails}
          area={area}
          updateArea={updateArea}
          isAreaSelected={isAreaSelected}
          materials={materials}
          categories={areaType?.categories}
          dismissedCategories={dismissedCategories}
          handleDismissCategory={handleDismissCategory}
          selectedPlaceholder={selectedPlaceholder}
          setSelectedPlaceholder={setSelectedPlaceholder}
          handleResetAddItems={handleResetAddItems}
          categoryFilterOptions={categoryFilterOptions}
          handleCategoryClick={handleCategoryClick}
          handleOpenCategoryFilter={handleOpenCategoryFilter}
          isLoading={isLoading}
          handleCheckArea={() => handleCheckArea(area, true)}
          openItemTemplateResults={openItemTemplateResults}
          showAreaDrawer={showAreaDrawer}
        />
      </AccordionDetails>
      <MiModal
        open={openItemTemplateModal}
        closeModal={handleCloseItemTemplateModal}
        title="Save as Item Template"
        description="Name the Item Template to save it for future use."
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: handleCloseItemTemplateModal,
          },
          {
            name: "Save",
            variant: BUTTON_VARIANTS.CONTAINED,
            color: BUTTON_COLORS.TEAL,
            onClick: handleCreateItemTemplate,
            disabled: !itemTemplateLabel || isLoading,
          },
        ]}
      >
        <MiInputTextbox
          placeholder="New Item Template Name"
          handleChange={(e) => setItemTemplateLabel(e.target.value)}
          value={itemTemplateLabel}
          inputProps={{ maxLength: 128 }}
        />
      </MiModal>
    </Accordion>
  );
};

const useStyles = makeStyles()((theme) => ({
  accordionContainer: {
    backgroundColor: theme.palette.common.white,
    width: "100%",
    margin: 0,
    padding: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: 4,
    "&::before": {
      content: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
    "& .MuiAccordion-heading": {
      margin: 0,
      alignItems: "center",
      width: "100%",
      flexDirection: "column",
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
      alignItems: "center",
      width: "100%",
      flexDirection: "column",
    },
    "& .MuiCollapse-root": {
      width: "100%",
    },
  },
  previewAccordion: {
    opacity: 0.5,
  },
  accordionSummary: {
    flexDirection: "column",
    paddingTop: theme.spacing(1),
    "&.Mui-focusVisible": {
      backgroundColor: theme.palette.common.white,
    },
  },
  areaActionsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    flex: 1,
  },
  areaDetailContainer: {
    width: "50%",
    paddingRight: theme.spacing(2),
  },
  areaLabelFieldRoot: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: theme.palette.common.black,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.common.black,
      fontWeight: theme.fontWeight.bold,
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: theme.palette.common.black,
      fontWeight: theme.fontWeight.bold,
    },
    padding: 0,
  },
  checkbox: {
    padding: theme.spacing(1),
  },
  editButtons: {
    display: "flex",
    flexDirection: "row",
  },
  rightActionButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));
